import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MODAL_SKLO } from '../../lib/uiTokens';

/**
 * SidebarTooltip — custom tooltip pro Activity Bar ikony.
 *
 * Native `title` atribut má dlouhý browser-default delay (~1500 ms na Windows)
 * — tady vyžadujeme rychlejší 350 ms feedback. Renderuje se přes portal nad
 * <body>, position fixed, vedle triggeru (default vpravo).
 *
 * Používá se jako wrapper kolem children (např. button v ActivityIcon).
 *
 * @param {Object} props
 * @param {string} props.label - Text tooltipu
 * @param {React.ReactNode} props.children - Trigger (ikona/button)
 * @param {number} [props.delay] - Hover delay v ms (default 350)
 * @param {string} [props.side] - 'right' (default) nebo 'left'
 */
export default function SidebarTooltip({
    label,
    children,
    delay = 350,
    side = 'right',
}) {
    const [show, setShow] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const timerRef = useRef(null);
    const wrapperRef = useRef(null);

    const computePosition = () => {
        const el = wrapperRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const top = rect.top + rect.height / 2;
        const left = side === 'right' ? rect.right + 10 : rect.left - 10;
        setPos({ top, left });
    };

    const handleEnter = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            computePosition();
            setShow(true);
        }, delay);
    };

    const handleLeave = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setShow(false);
    };

    // Bezpečnost: clear timer při unmount
    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    if (!label) return children;

    return (
        <span
            ref={wrapperRef}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            style={{ display: 'inline-flex' }}
        >
            {children}
            {show && createPortal(
                <div
                    role="tooltip"
                    style={{
                        position: 'fixed',
                        top: pos.top,
                        left: pos.left,
                        transform: side === 'right' ? 'translateY(-50%)' : 'translate(-100%, -50%)',
                        ...MODAL_SKLO,
                        borderRadius: 6,
                        color: 'var(--text-primary)',
                        padding: '6px 10px',
                        fontSize: 12,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        zIndex: 10000,
                        pointerEvents: 'none',
                        animation: 'sidebar-tooltip-in 0.12s ease-out',
                    }}
                >
                    {label}
                </div>,
                document.body,
            )}
            <style>{`
                @keyframes sidebar-tooltip-in {
                    from { opacity: 0; transform: ${side === 'right' ? 'translate(-4px, -50%)' : 'translate(calc(-100% + 4px), -50%)'}; }
                    to { opacity: 1; transform: ${side === 'right' ? 'translateY(-50%)' : 'translate(-100%, -50%)'}; }
                }
            `}</style>
        </span>
    );
}
